import { TypedDocumentNode, gql } from '@apollo/client';
import { Fragment } from 'react';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';

import { CompetitionHeader } from '../CompetitionHeader';
import { LineupImportStatus } from '../LineupImportStatus';
import { LineupItem } from '../LineupItem';
import { So5LeagueWrapper } from '../So5LeagueWrapper';
import { useHierarchiedLineups } from '../useHierarchiedLineups';
import { ImportedLineups_so5LineupImport } from './__generated__/index.graphql';

type Props = {
  importedLineups: ImportedLineups_so5LineupImport[];
};

export const ImportedLineups = ({ importedLineups }: Props) => {
  const hierarchiedLineups = useHierarchiedLineups(importedLineups);
  return (
    <Vertical gap={2}>
      {hierarchiedLineups.map(({ competitions, so5League }) => (
        <So5LeagueWrapper key={so5League.slug} so5League={so5League}>
          {competitions?.map(({ mainRarityType, seasonality, lineups }) => (
            <Fragment
              key={`${so5League.slug}-${mainRarityType}-${seasonality}`}
            >
              <CompetitionHeader
                rarity={mainRarityType}
                seasonality={seasonality}
                count={lineups.length}
              />
              {lineups.map(({ id, so5Leaderboard, appearances, aasmState }) => (
                <LineupItem
                  key={id}
                  iconUrl={
                    so5Leaderboard?.divisionIconUrl || so5Leaderboard?.iconUrl
                  }
                  rarity={so5Leaderboard?.mainRarityType}
                  appearances={appearances.map(a => ({
                    id: a.composeTeamObject.id,
                    index: a.index,
                    captain: a.captain,
                    playerDisplayName:
                      a.composeTeamObject.anyPlayer.displayName,
                    avatarPictureUrl:
                      a.composeTeamObject.anyPlayer.avatarPictureUrl,
                  }))}
                  status={<LineupImportStatus status={aasmState} />}
                />
              ))}
            </Fragment>
          ))}
        </So5LeagueWrapper>
      ))}
    </Vertical>
  );
};

ImportedLineups.fragments = {
  so5LineupImport: gql`
    fragment ImportedLineups_so5LineupImport on So5LineupImport {
      id
      aasmState
      appearances {
        index
        captain
        composeTeamObject {
          id
          anyPlayer {
            slug
            avatarPictureUrl
            displayName
          }
        }
      }
      so5Leaderboard {
        slug
        seasonality
        iconUrl
        divisionIconUrl
        mainRarityType
        so5League {
          slug
          ...So5LeagueWrapper_so5League
        }
      }
      ...useHierarchiedLineups_so5LineupImport
    }
    ${So5LeagueWrapper.fragments.so5League}
    ${useHierarchiedLineups.fragments.so5LineupImport}
  ` as TypedDocumentNode<ImportedLineups_so5LineupImport>,
};
